import React, { useEffect, useRef, useState } from 'react';

import { DIAG_WAIVER_COPY as DWC, formatDiagWaiverCopy as fmtCopy } from '../../../constants/diagWaiverCopy';
import { formatPaise } from '../../../utils/money';
import { isInSettlementWindow } from '../../../constants/admission';
import { recordDiagnosticWaiver } from '../../../services/api/diagWaiverApi';
// No second idempotency-key scheme — FE-A deliberately left the generator
// out of diagWaiverApi.js; reuse the shipped `credit-settle:` precedent
// rather than inventing a `diag-waiver-record:` one.
// QA D4 fix — `RunningBillResponse` has no `unsettled_lab_paise` field (the
// bill read never carried it; only the write endpoints did), so the "Diagnostics
// outstanding" box was always fed `undefined` and never rendered on the real
// screen. The REAL figure the settle card already proves exists on the client
// is `GET /credits`'s own server-computed `total_outstanding_paise` (module
// LAB today) — this widget reads it directly rather than trusting a caller-
// supplied prop that two different mount points (RunningBillPanel's own
// header, the workspace task bar) would have to duplicate or forget.
import { listOutstandingCredits, newCreditSettleIdempotencyKey } from '../../../services/api/creditsApi';
import { withEmphasis } from '../../../utils/richCopy';

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto';
const btnPrimary = 'px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold disabled:opacity-60';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const inputCls = 'w-full px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-sm';
const labelCls = 'block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1';
const entryBtnCls = 'px-3 py-1.5 rounded-lg border border-indigo-400 dark:border-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200 text-[12.5px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed';

/**
 * Every disabled state names its OWN reason (owner's standing rule — never a
 * generic "not allowed"). Checked in a fixed priority so exactly one reason
 * shows at a time. `admission_id` absence ("walk-in") can never actually
 * happen at this dialog's one callsite (RunningBillPanel only ever mounts
 * for a real admission) — kept anyway because it is the SAME shape check
 * SettleCreditsCard's per-line waiver box already makes, so the two surfaces
 * cannot silently diverge if this dialog is ever reused for a walk-in-shaped
 * caller. `WAIVER_ALREADY_ACTIVE` is NOT pre-computed here (v5 §1.4 does not
 * freeze whether the running-bill read carries the recorded-by name/date) —
 * it surfaces reactively from the server's 409 at submit, the same
 * client-hint/server-authoritative split `WaiverConcessionDialog` already
 * uses for its own cap.
 *
 * `recordedPaise`/`reversedPaise`/`appliedPaise` (all optional) are the SAME
 * `diag_waiver_*` scalars the bill surfaces read (`buildDiagWaiverLine`) —
 * when the caller has them (RunningBillPanel's own `bill`), a waiver that has
 * been FULLY APPLIED (§13.11 — the one-per-admission limitation, `bk-1064`)
 * disables the entry button pre-emptively instead of letting the admin walk
 * into the server's refusal. Absent (e.g. the workspace task-bar mount, which
 * has no bill loaded) → fails OPEN, same convention as every other
 * not-yet-known disable check in this codebase; the server's own
 * `spentNoFurther` refusal is the real, money-safe wall either way.
 */
function resolveDisabledReason({
  canRecord, admission, recordedPaise, reversedPaise, appliedPaise,
}) {
  if (!canRecord) return { code: 'nonAdmin', text: DWC['diagWaiver.record.disabled.nonAdmin'] };
  if (!admission || !admission.admission_id) return { code: 'walkIn', text: DWC['diagWaiver.record.disabled.walkIn'] };
  if (!isInSettlementWindow(admission.status)) return { code: 'stayClosed', text: DWC['diagWaiver.record.disabled.stayClosed'] };
  const recorded = Number(recordedPaise) || 0;
  const reversed = Number(reversedPaise) || 0;
  const applied = Number(appliedPaise) || 0;
  const remaining = Math.max(recorded - reversed - applied, 0);
  if (recorded > 0 && applied > 0 && remaining === 0) {
    return {
      code: 'spent',
      text: DWC['diagWaiver.record.disabled.spent'],
      tooltip: DWC['diagWaiver.record.disabled.spent.tooltip'],
    };
  }
  return null;
}

/** Machine `code` → the shared copy source's own wording. Never a re-typed message. */
function mapRecordError(res) {
  switch (res.code) {
    case 'WAIVER_ALREADY_ACTIVE': {
      // §13.11 (bk-1064) — the ONE code covers two genuinely different
      // situations, split on `remaining_paise` (the server's own response
      // field, never a client guess): some of the recorded waiver is still
      // UNAPPLIED ("…another cannot be recorded until then" is true), vs the
      // waiver is FULLY APPLIED, where "until then" is a moment that never
      // arrives — that case gets the dedicated `spentNoFurther` string
      // instead, with no false promise of a later opening.
      if (Number(res.remaining_paise) === 0) return DWC['diagWaiver.record.server.spentNoFurther'];
      // §13.2/§13.5 rewrite (2026-09-07): pair what's recorded + why another
      // can't be added (`record.server.alreadyActive`) with the honest
      // correction path (`record.alreadyActive.correction`) — no reverse
      // action is offered, because none exists in this release.
      return `${fmtCopy('diagWaiver.record.server.alreadyActive', { recorded: formatPaise(res.recorded_paise) })} ${DWC['diagWaiver.record.alreadyActive.correction']}`;
    }
    case 'WAIVER_EXCEEDS_OUTSTANDING':
      return fmtCopy('diagWaiver.record.server.exceedsOutstanding', { maxAmount: formatPaise(res.max_recordable_paise) });
    case 'WAIVER_ADMITTED_ONLY':
      return DWC['diagWaiver.record.server.walkInNotAllowed'];
    case 'ADMISSION_NOT_ACTIVE':
      return DWC['diagWaiver.record.server.stayClosed'];
    case 'DISCOUNT_NOT_PERMITTED':
      return DWC['diagWaiver.record.server.notPermitted'];
    case 'IPD_TABLE_UNAVAILABLE':
      return DWC['diagWaiver.record.server.unavailable'];
    default:
      return res.error || DWC['diagWaiver.record.server.unavailable'];
  }
}

/**
 * DiagnosticWaiverDialog (DIAG-DISCOUNT v5, FE-B) — the RECORD dialog. An
 * ADMIN records ONE amount + ONE mandatory reason against this
 * admitted stay's diagnostics; **nothing moves** — no ledger row, no
 * Payment, no balance change. A receptionist settles later and the SERVER
 * allocates the recorded amount pro-rata (§1 CONTRACT) — this dialog never
 * previews or computes that split; the client-side spread preview
 * (`spreadWaiverAcrossLines`) that the pre-v5 one-amount dialog carried is
 * GONE, on purpose (the server owns allocation now).
 *
 * Self-contained widget: owns the entry button (always rendered, disabled
 * with its own reason when it can't be used — never hidden, never a bare
 * "not allowed") AND the modal, so a caller only ever writes one line:
 * `<DiagnosticWaiverDialog admission={admission} canRecord={...} />`.
 *
 * Two-step commit — fill the form, then a SEPARATE confirm screen restates
 * "no money is collected now" before the actual POST. The cap against the
 * stay's outstanding is the SERVER's (`WAIVER_EXCEEDS_OUTSTANDING`) — this
 * dialog never clamps or pre-computes a ceiling client-side.
 *
 * @param admission        { admission_id, status, ... } — the stay's row.
 * @param canRecord        bool — ADMIN authority. There is NO `MANAGER` role
 *                         (`ClinicRole` is ADMIN · DOCTOR · RECEPTIONIST ·
 *                         NURSE · LAB_TECHNICIAN · BILLING · ACCOUNTANT);
 *                         build-plan-v5 §1.1 root-caused that mis-reading and
 *                         it must not resurface. Server authority is
 *                         `enforce_diagnostics_discount_authority` (ADMIN via
 *                         FULL_ACCESS);
 *                         caller passes `usePermission(CHARGE_PERMISSIONS.GRANT_CONCESSION)`,
 *                         the SAME slug "Waiver / concession" already gates).
 * @param recordedPaise/reversedPaise/appliedPaise  number|undefined — the
 *                         SAME `diag_waiver_*` scalars the bill line reads
 *                         (`buildDiagWaiverLine`); optional — only used to
 *                         pre-emptively disable the entry button once the
 *                         active waiver is fully spent (§13.11). Fails open
 *                         when the caller doesn't have them.
 * @param onRecorded(data) — called once, after a successful 201/200, so the
 *                         caller can refresh its read-only figures. Recording
 *                         moves no money, so this is a courtesy refresh, not
 *                         a balance reload.
 */
export default function DiagnosticWaiverDialog({
  admission, canRecord, recordedPaise, reversedPaise, appliedPaise, onRecorded,
}) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'confirm'
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [unsettledLabPaise, setUnsettledLabPaise] = useState(null);
  const idemKeyRef = useRef(null);

  const disabledInfo = resolveDisabledReason({
    canRecord, admission, recordedPaise, reversedPaise, appliedPaise,
  });

  // QA D4 fix — self-contained fetch (never a caller-supplied prop two
  // different mount points would have to duplicate): ONE bounded, single-
  // flight read of the REAL outstanding figure `GET /credits` already
  // computes server-side, keyed on the primitive `admission_id` (never the
  // `admission` object — an object recreated every render must never sit in
  // an effect dep array, that is the request-storm trap). Skipped entirely
  // when the widget can't be used anyway (non-admin/walk-in/closed stay), so
  // a receptionist's screen never fires this call at all.
  const admissionId = admission?.admission_id;
  const isUsable = !disabledInfo;
  const outstandingFetchedForRef = useRef(null);
  useEffect(() => {
    if (!isUsable || !admissionId) return;
    if (outstandingFetchedForRef.current === admissionId) return;
    outstandingFetchedForRef.current = admissionId;
    let alive = true;
    (async () => {
      const res = await listOutstandingCredits({ admissionId });
      if (alive && res && res.success) setUnsettledLabPaise(res.data.totalOutstandingPaise);
    })();
    return () => { alive = false; };
  }, [isUsable, admissionId]);

  const openDialog = () => {
    if (disabledInfo) return;
    idemKeyRef.current = newCreditSettleIdempotencyKey();
    setAmount('');
    setReason('');
    setStep('form');
    setError(null);
    setDone(null);
    setOpen(true);
  };
  const close = () => setOpen(false);

  const amountRaw = amount.trim();
  const amountNum = Number(amountRaw);
  // Owner's standing rule: a money input must never quietly do something
  // other than what was typed.
  const amountInvalid = amountRaw !== '' && (!Number.isFinite(amountNum) || amountNum <= 0);
  const amountPaise = amountInvalid ? 0 : Math.round(amountNum * 100);
  const amountValid = amountRaw !== '' && !amountInvalid && amountPaise > 0;
  const reasonValid = reason.trim().length > 0;
  const canProceed = amountValid && reasonValid;

  const goToConfirm = () => {
    if (!canProceed) return;
    setError(null);
    setStep('confirm');
  };
  const backToForm = () => setStep('form');

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const res = await recordDiagnosticWaiver(admission.admission_id, {
      amount_paise: amountPaise,
      reason: reason.trim(),
      idempotency_key: idemKeyRef.current,
    });
    setBusy(false);
    if (res.success) {
      setDone(res.data || {});
      if (typeof onRecorded === 'function') onRecorded(res.data);
      return;
    }
    setError(mapRecordError(res));
  };

  // §13.11 (bk-1064) — once the recorded waiver is fully spent, the button's
  // OWN label states the rule (mirrors §13.2's `alreadyActive.button`
  // pattern); every other disabled reason keeps the default label with the
  // reason only on the title, unchanged.
  const buttonLabel = disabledInfo?.code === 'spent'
    ? DWC['diagWaiver.record.disabled.spent']
    : DWC['diagWaiver.record.button.label'];
  const buttonTitle = disabledInfo
    ? (disabledInfo.tooltip || disabledInfo.text)
    : DWC['diagWaiver.record.button.tooltip'];

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        disabled={!!disabledInfo}
        title={buttonTitle}
        className={entryBtnCls}
        data-testid="diag-waiver-record-open"
      >
        {buttonLabel}
      </button>

      {open && (
        <div className={overlayCls} data-testid="diag-waiver-record-dialog">
          <div className={panelCls}>
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center">
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {done ? DWC['diagWaiver.record.success.title'] : DWC['diagWaiver.record.title']}
              </h3>
              <button type="button" onClick={close} className="ml-auto text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
            </div>

            {done ? (
              <>
                <div className="p-4 space-y-3" data-testid="diag-waiver-record-success">
                  <p className="text-[13px] text-slate-700 dark:text-slate-200">
                    {fmtCopy('diagWaiver.record.success.body', { recorded: formatPaise(done.recorded_paise ?? amountPaise) })}
                  </p>
                </div>
                <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
                  <button type="button" className={btnSecondary} onClick={close} data-testid="diag-waiver-record-done">
                    {DWC['diagWaiver.record.success.done']}
                  </button>
                </div>
              </>
            ) : step === 'confirm' ? (
              <>
                <div className="p-4 space-y-3">
                  <div className="rounded-lg border border-teal-300 dark:border-teal-700 bg-teal-50 dark:bg-teal-900/20 p-3 space-y-1" data-testid="diag-waiver-record-confirm-block">
                    <p className="text-[12.5px] font-semibold text-teal-800 dark:text-teal-200">{DWC['diagWaiver.record.confirm.title']}</p>
                    <p className="text-[12px] text-teal-800 dark:text-teal-200">
                      {withEmphasis(fmtCopy('diagWaiver.record.confirm.body', { recorded: formatPaise(amountPaise) }))}
                    </p>
                    {/* §13.11 (bk-1064) — the point of no return names the
                        one-per-admission constraint too. */}
                    <p className="text-[12px] text-teal-800 dark:text-teal-200" data-testid="diag-waiver-record-confirm-once-per-stay">
                      {DWC['diagWaiver.record.oncePerStay.confirm']}
                    </p>
                  </div>
                  {error && (
                    <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="diag-waiver-record-error">{error}</div>
                  )}
                </div>
                <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
                  <button type="button" className={btnSecondary} onClick={backToForm} data-testid="diag-waiver-record-confirm-cancel">
                    {DWC['diagWaiver.record.confirm.cancel']}
                  </button>
                  <button type="button" className={btnPrimary} onClick={submit} disabled={busy} data-testid="diag-waiver-record-confirm">
                    {busy ? DWC['diagWaiver.record.submit.busy'] : DWC['diagWaiver.record.confirm.action']}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="p-4 space-y-3">
                  {/* The point of the whole dialog — impossible to miss, never
                      tucked under a field. */}
                  <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-[12.5px] p-3" data-testid="diag-waiver-record-intro">
                    {withEmphasis(DWC['diagWaiver.record.intro'])}
                  </div>

                  {unsettledLabPaise != null && (
                    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-[12.5px] text-slate-600 dark:text-slate-300 px-3 py-2" data-testid="diag-waiver-record-outstanding">
                      {DWC['diagWaiver.record.outstanding.label']}: <b className="text-slate-900 dark:text-white tabular-nums">{formatPaise(unsettledLabPaise)}</b>
                      <div className="mt-1">{DWC['diagWaiver.record.outstanding.hint']}</div>
                    </div>
                  )}

                  {/* §13.11 (bk-1064) — warned BEFORE the admin commits an
                      amount, not only in the refusal they'd meet days later.
                      Above the amount field on purpose: the moment they pick
                      the number is the moment this matters. */}
                  <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-[12px] p-2.5" data-testid="diag-waiver-record-once-per-stay">
                    {withEmphasis(DWC['diagWaiver.record.oncePerStay.notice'])}
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="dwr-amount">{DWC['diagWaiver.record.amount.label']}</label>
                    <input
                      id="dwr-amount"
                      type="text"
                      inputMode="decimal"
                      className={`${inputCls} max-w-[180px]`}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      data-testid="diag-waiver-record-amount"
                    />
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{DWC['diagWaiver.record.amount.helper']}</p>
                    {amountInvalid && (
                      <p className="text-[12px] text-red-600 dark:text-red-400 mt-1.5" data-testid="diag-waiver-record-amount-invalid">{DWC['diagWaiver.error.amountInvalid']}</p>
                    )}
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="dwr-reason">
                      {DWC['diagWaiver.record.reason.label']} <span className="text-red-500">{DWC['diagWaiver.field.reason.requiredMark']}</span>
                    </label>
                    <textarea
                      id="dwr-reason"
                      className={inputCls}
                      rows={2}
                      maxLength={300}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      data-testid="diag-waiver-record-reason"
                    />
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{DWC['diagWaiver.record.reason.helper']}</p>
                  </div>

                  <p className="text-[11px] text-slate-500 dark:text-slate-400">{DWC['diagWaiver.record.correction.note']}</p>

                  {error && (
                    <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="diag-waiver-record-error">{error}</div>
                  )}
                </div>
                <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
                  <button type="button" className={btnSecondary} onClick={close} data-testid="diag-waiver-record-cancel">Cancel</button>
                  <button type="button" className={btnPrimary} onClick={goToConfirm} disabled={!canProceed} data-testid="diag-waiver-record-submit">
                    {DWC['diagWaiver.record.submit']}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
